import { GrowthBook, useGrowthBook } from '@growthbook/growthbook-react';
import { AppFeatures } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import { useMemo } from 'react';

type BooleanFeature = { [K in keyof AppFeatures]: AppFeatures[K] extends boolean ? K : never }[keyof AppFeatures];

let mirror: GrowthBook<AppFeatures> | null = null;

/**
 * Reads a flag through a mirror of the live SDK built without a trackingCallback, so it logs no
 * exposure. For screens that must follow another screen's experiment without joining it: the
 * product page names checkout's first provider, but a product view must not count as a checkout
 * exposure (DEV-2352). Only ever fed the payload the live instance already fetched.
 */
export const useUntrackedFeatureIsOn = (feature: BooleanFeature): boolean => {
  const growthbook = useGrowthBook<AppFeatures>();
  const payload = growthbook?.getPayload();
  const attributes = growthbook?.getAttributes();
  // both getters return a fresh empty object while nothing is forced, so the memo keys on their contents
  const forcedFeatures = JSON.stringify(Array.from(growthbook?.getForcedFeatures() ?? new Map()));
  const forcedVariations = JSON.stringify(growthbook?.getForcedVariations() ?? {});

  return useMemo(() => {
    try {
      if (!mirror) {
        mirror = new GrowthBook<AppFeatures>({});
      }

      mirror.initSync({ payload });
      void mirror.setAttributes(attributes);
      mirror.setForcedFeatures(new Map(JSON.parse(forcedFeatures)));
      mirror.setForcedVariations(JSON.parse(forcedVariations));

      return mirror.isOn(feature);
    } catch {
      return false;
    }
  }, [payload, attributes, forcedFeatures, forcedVariations, feature]);
};
